import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  GraduationCap, 
  FlaskConical, 
  HeadphonesIcon, 
  RefreshCw, 
  BadgeCheck, 
  TrendingUp 
} from 'lucide-react';

const differentials = [
  {
    icon: FlaskConical,
    title: 'Método Prático',
    description: 'Aprenda fazendo. Nossas aulas são 80% práticas com modelos reais desde o primeiro dia.',
  },
  {
    icon: BadgeCheck,
    title: 'Protocolos Testados',
    description: 'Todos os protocolos são validados e utilizados em nossa clínica com resultados comprovados.',
  },
  {
    icon: GraduationCap,
    title: 'Formação Profissional',
    description: 'Certificação reconhecida no mercado que abre portas para as melhores oportunidades.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Suporte ao Aluno',
    description: 'Grupo exclusivo com suporte contínuo e mentoria mesmo após a conclusão do curso.',
  },
  {
    icon: RefreshCw,
    title: 'Atualização Técnica',
    description: 'Acesso a atualizações e novidades do mercado sem custo adicional por 1 ano.',
  },
  {
    icon: TrendingUp,
    title: 'Posicionamento',
    description: 'Aprenda a se posicionar no mercado, precificar e atrair clientes de alto padrão.',
  },
];

const DifferentialCard = ({ item, index, isInView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative p-6 lg:p-8 text-center"
      data-testid={`differential-${index}`}
    >
      {/* Icon */}
      <motion.div
        whileHover={{ scale: 1.1, rotate: 5 }}
        className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center group-hover:from-gold/30 group-hover:to-gold/10 transition-all"
      >
        <item.icon className="w-8 h-8 text-gold" />
      </motion.div>

      {/* Title */}
      <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-gold transition-colors">
        {item.title}
      </h3>

      {/* Description */}
      <p className="text-white/60 text-sm leading-relaxed">
        {item.description}
      </p>

      {/* Hover line */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gold origin-center"
      />
    </motion.div>
  );
};

export const Differentials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="diferenciais"
      ref={ref}
      data-testid="differentials-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark-paper" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-gold font-medium tracking-wider uppercase text-sm mb-4">
            Por que nos escolher
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Nossos <span className="text-gold">Diferenciais</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            O que faz a Ritha Barreto Academy ser referência na formação de profissionais de estética.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {differentials.map((item, index) => (
            <div
              key={index}
              className="glass-card rounded-2xl"
            >
              <DifferentialCard item={item} index={index} isInView={isInView} />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-white/50 text-sm mb-4">
            Junte-se a mais de 2.500 profissionais que transformaram suas carreiras
          </p>
          <motion.a
            href="#contato"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contato')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-gold text-black font-bold px-8 py-4 rounded-full hover:bg-gold-light transition-all btn-gold-glow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="differentials-cta"
          >
            Começar minha transformação
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
